import type { PersonApi } from "../../api/PersonApi";
import type { AddressStaff } from "../../generated/dto/AddressStaff";
import type { AgreementFetchOptions } from "../../generated/dto/AgreementFetchOptions";
import type { AgreementInstanceFetchOptions } from "../../generated/dto/AgreementInstanceFetchOptions";
import type { AgreementInstanceStaff } from "../../generated/dto/AgreementInstanceStaff";
import type { AgreementStaff } from "../../generated/dto/AgreementStaff";
import type { ApClassFetchOptions } from "../../generated/dto/ApClassFetchOptions";
import type { ApClassSignupStaff } from "../../generated/dto/ApClassSignupStaff";
import type { ApClassSignupStaffFetchOptions } from "../../generated/dto/ApClassSignupStaffFetchOptions";
import type { ApClassStaff } from "../../generated/dto/ApClassStaff";
import type { ApClassTypeOverrideFetchOptions } from "../../generated/dto/ApClassTypeOverrideFetchOptions";
import type { ApClassTypeOverrideStaff } from "../../generated/dto/ApClassTypeOverrideStaff";
import type { AssessmentStaff } from "../../generated/dto/AssessmentStaff";
import type { BoatTypeStaff } from "../../generated/dto/BoatTypeStaff";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { CreatePersonAddress } from "../../generated/dto/CreatePersonAddress";
import type { CreatePersonMember } from "../../generated/dto/CreatePersonMember";
import type { CreatePersonPersonType } from "../../generated/dto/CreatePersonPersonType";
import type { CreatePersonPhone } from "../../generated/dto/CreatePersonPhone";
import type { CreatePersonReferral } from "../../generated/dto/CreatePersonReferral";
import type { CreatePersonRelation } from "../../generated/dto/CreatePersonRelation";
import type { CreatePersonStaff } from "../../generated/dto/CreatePersonStaff";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DeletePersonAddressStaff } from "../../generated/dto/DeletePersonAddressStaff";
import type { DeletePersonPersonTypeStaff } from "../../generated/dto/DeletePersonPersonTypeStaff";
import type { DeletePersonPhoneStaff } from "../../generated/dto/DeletePersonPhoneStaff";
import type { DeletePersonReferralStaff } from "../../generated/dto/DeletePersonReferralStaff";
import type { DeletePersonRelationStaff } from "../../generated/dto/DeletePersonRelationStaff";
import type { DeletePersonStaff } from "../../generated/dto/DeletePersonStaff";
import type { JpClassFetchOptions } from "../../generated/dto/JpClassFetchOptions";
import type { JpClassSignupFetchOptions } from "../../generated/dto/JpClassSignupFetchOptions";
import type { JpClassSignupStaff } from "../../generated/dto/JpClassSignupStaff";
import type { JpClassStaff } from "../../generated/dto/JpClassStaff";
import type { JpClassTypeOverrideFetchOptions } from "../../generated/dto/JpClassTypeOverrideFetchOptions";
import type { JpClassTypeOverrideStaff } from "../../generated/dto/JpClassTypeOverrideStaff";
import type { MemberProfile } from "../../generated/dto/MemberProfile";
import type { MemberProfileCreate } from "../../generated/dto/MemberProfileCreate";
import type { MemberProfileUpdate } from "../../generated/dto/MemberProfileUpdate";
import type { MembershipMember } from "../../generated/dto/MembershipMember";
import type { MembershipMemberFetchOptions } from "../../generated/dto/MembershipMemberFetchOptions";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { PersonAddressFetchOptions } from "../../generated/dto/PersonAddressFetchOptions";
import type { PersonAddressSearchParams } from "../../generated/dto/PersonAddressSearchParams";
import type { PersonAddressStaff } from "../../generated/dto/PersonAddressStaff";
import type { PersonAddressStaffSearchResult } from "../../generated/dto/PersonAddressStaffSearchResult";
import type { PersonAgreementInstanceFetchOptions } from "../../generated/dto/PersonAgreementInstanceFetchOptions";
import type { PersonAgreementInstanceSearchParams } from "../../generated/dto/PersonAgreementInstanceSearchParams";
import type { PersonAgreementInstanceStaff } from "../../generated/dto/PersonAgreementInstanceStaff";
import type { PersonAgreementInstanceStaffSearchResult } from "../../generated/dto/PersonAgreementInstanceStaffSearchResult";
import type { PersonAgreementsPage } from "../../generated/dto/PersonAgreementsPage";
import type { PersonCheckInFetchOptions } from "../../generated/dto/PersonCheckInFetchOptions";
import type { PersonCheckInStaff } from "../../generated/dto/PersonCheckInStaff";
import type { PersonCommentFetchOptions } from "../../generated/dto/PersonCommentFetchOptions";
import type { PersonCommentSearchParams } from "../../generated/dto/PersonCommentSearchParams";
import type { PersonCommentStaff } from "../../generated/dto/PersonCommentStaff";
import type { PersonCommentStaffSearchResult } from "../../generated/dto/PersonCommentStaffSearchResult";
import type { PersonCreateEmergencyContactRequest } from "../../generated/dto/PersonCreateEmergencyContactRequest";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonGetPersonNameRequest } from "../../generated/dto/PersonGetPersonNameRequest";
import type { PersonGrantRatingRequest } from "../../generated/dto/PersonGrantRatingRequest";
import type { PersonMember } from "../../generated/dto/PersonMember";
import type { PersonMemberFetchOptions } from "../../generated/dto/PersonMemberFetchOptions";
import type { PersonMemberSearchParams } from "../../generated/dto/PersonMemberSearchParams";
import type { PersonMemberSearchResult } from "../../generated/dto/PersonMemberSearchResult";
import type { PersonPersonTypeFetchOptions } from "../../generated/dto/PersonPersonTypeFetchOptions";
import type { PersonPersonTypeStaff } from "../../generated/dto/PersonPersonTypeStaff";
import type { PersonPhoneFetchOptions } from "../../generated/dto/PersonPhoneFetchOptions";
import type { PersonPhoneSearchParams } from "../../generated/dto/PersonPhoneSearchParams";
import type { PersonPhoneStaff } from "../../generated/dto/PersonPhoneStaff";
import type { PersonPhoneStaffSearchResult } from "../../generated/dto/PersonPhoneStaffSearchResult";
import type { PersonRatingFetchOptions } from "../../generated/dto/PersonRatingFetchOptions";
import type { PersonRatingStaff } from "../../generated/dto/PersonRatingStaff";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonReferralSearchParams } from "../../generated/dto/PersonReferralSearchParams";
import type { PersonReferralStaff } from "../../generated/dto/PersonReferralStaff";
import type { PersonReferralStaffSearchResult } from "../../generated/dto/PersonReferralStaffSearchResult";
import type { PersonRelationFetchOptions } from "../../generated/dto/PersonRelationFetchOptions";
import type { PersonRelationRef } from "../../generated/dto/PersonRelationRef";
import type { PersonRelationSearchParams } from "../../generated/dto/PersonRelationSearchParams";
import type { PersonRelationStaff } from "../../generated/dto/PersonRelationStaff";
import type { PersonRelationStaffSearchResult } from "../../generated/dto/PersonRelationStaffSearchResult";
import type { PersonRelationTypeStaff } from "../../generated/dto/PersonRelationTypeStaff";
import type { PersonRemoveCommentRequest } from "../../generated/dto/PersonRemoveCommentRequest";
import type { PersonRemovePersonAddressRequest } from "../../generated/dto/PersonRemovePersonAddressRequest";
import type { PersonRemovePersonPhoneRequest } from "../../generated/dto/PersonRemovePersonPhoneRequest";
import type { PersonRemoveSkillRequest } from "../../generated/dto/PersonRemoveSkillRequest";
import type { PersonRescindRatingRequest } from "../../generated/dto/PersonRescindRatingRequest";
import type { PersonSaveCommentRequest } from "../../generated/dto/PersonSaveCommentRequest";
import type { PersonSaveSkillRequest } from "../../generated/dto/PersonSaveSkillRequest";
import type { PersonSearchPersonAgreementsRequest } from "../../generated/dto/PersonSearchPersonAgreementsRequest";
import type { PersonSetAdminHoldRequest } from "../../generated/dto/PersonSetAdminHoldRequest";
import type { PersonSetTagsRequest } from "../../generated/dto/PersonSetTagsRequest";
import type { PersonSignAgreementRequest } from "../../generated/dto/PersonSignAgreementRequest";
import type { PersonSkillChangeFetchOptions } from "../../generated/dto/PersonSkillChangeFetchOptions";
import type { PersonSkillChangeSearchParams } from "../../generated/dto/PersonSkillChangeSearchParams";
import type { PersonSkillChangeStaff } from "../../generated/dto/PersonSkillChangeStaff";
import type { PersonSkillChangeStaffSearchResult } from "../../generated/dto/PersonSkillChangeStaffSearchResult";
import type { PersonSkillFetchOptions } from "../../generated/dto/PersonSkillFetchOptions";
import type { PersonSkillSearchParams } from "../../generated/dto/PersonSkillSearchParams";
import type { PersonSkillStaff } from "../../generated/dto/PersonSkillStaff";
import type { PersonSkillStaffSearchResult } from "../../generated/dto/PersonSkillStaffSearchResult";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PersonStaffSearchParams } from "../../generated/dto/PersonStaffSearchParams";
import type { PersonStaffSearchResult } from "../../generated/dto/PersonStaffSearchResult";
import type { PersonTagFetchOptions } from "../../generated/dto/PersonTagFetchOptions";
import type { PersonTagSearchParams } from "../../generated/dto/PersonTagSearchParams";
import type { PersonTagStaff } from "../../generated/dto/PersonTagStaff";
import type { PersonTagStaffSearchResult } from "../../generated/dto/PersonTagStaffSearchResult";
import type { PersonTypeStaff } from "../../generated/dto/PersonTypeStaff";
import type { PhoneStaff } from "../../generated/dto/PhoneStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { RatingChangeFetchOptions } from "../../generated/dto/RatingChangeFetchOptions";
import type { RatingChangeSearchParams } from "../../generated/dto/RatingChangeSearchParams";
import type { RatingChangeStaff } from "../../generated/dto/RatingChangeStaff";
import type { RatingChangeStaffSearchResult } from "../../generated/dto/RatingChangeStaffSearchResult";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { SavePersonAddress } from "../../generated/dto/SavePersonAddress";
import type { SavePersonPhone } from "../../generated/dto/SavePersonPhone";
import type { SignoutCrewFetchOptions } from "../../generated/dto/SignoutCrewFetchOptions";
import type { SignoutCrewSearchParams } from "../../generated/dto/SignoutCrewSearchParams";
import type { SignoutCrewStaff } from "../../generated/dto/SignoutCrewStaff";
import type { SignoutCrewStaffSearchResult } from "../../generated/dto/SignoutCrewStaffSearchResult";
import type { SignoutFetchOptions } from "../../generated/dto/SignoutFetchOptions";
import type { SignoutMember } from "../../generated/dto/SignoutMember";
import type { SignoutMemberFetchOptions } from "../../generated/dto/SignoutMemberFetchOptions";
import type { SignoutMemberSearchParams } from "../../generated/dto/SignoutMemberSearchParams";
import type { SignoutMemberSearchResult } from "../../generated/dto/SignoutMemberSearchResult";
import type { SignoutSearchParams } from "../../generated/dto/SignoutSearchParams";
import type { SignoutStaff } from "../../generated/dto/SignoutStaff";
import type { SignoutStaffSearchResult } from "../../generated/dto/SignoutStaffSearchResult";
import type { SkillAssessmentFetchOptions } from "../../generated/dto/SkillAssessmentFetchOptions";
import type { SkillAssessmentStaff } from "../../generated/dto/SkillAssessmentStaff";
import type { SkillSearchParams } from "../../generated/dto/SkillSearchParams";
import type { SkillStaff } from "../../generated/dto/SkillStaff";
import type { SkillStaffSearchResult } from "../../generated/dto/SkillStaffSearchResult";
import type { StaffProfileCreateRequest } from "../../generated/dto/StaffProfileCreateRequest";
import type { StaffProfileUpdateRequest } from "../../generated/dto/StaffProfileUpdateRequest";
import type { TagStaff } from "../../generated/dto/TagStaff";
import type { UpdatePersonAddress } from "../../generated/dto/UpdatePersonAddress";
import type { UpdatePersonMember } from "../../generated/dto/UpdatePersonMember";
import type { UpdatePersonPersonType } from "../../generated/dto/UpdatePersonPersonType";
import type { UpdatePersonPhone } from "../../generated/dto/UpdatePersonPhone";
import type { UpdatePersonReferral } from "../../generated/dto/UpdatePersonReferral";
import type { UpdatePersonRelation } from "../../generated/dto/UpdatePersonRelation";
import type { UpdatePersonStaff } from "../../generated/dto/UpdatePersonStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PersonFrontendService {
  constructor(private readonly api: PersonApi) {}
  private readonly personRelationTypeStaffCache: Map<number, PersonRelationTypeStaff> = new Map();
  private readonly skillStaffCache: Map<number, SkillStaff> = new Map();
  private readonly assessmentStaffCache: Map<number, AssessmentStaff> = new Map();
  private readonly skillAssessmentStaffCache: Map<number, SkillAssessmentStaff> = new Map();
  async getPersonMember(id: number, options?: PersonMemberFetchOptions): Promise<PersonMember> {
    return this.api.getPersonMember(id, options)
  }
  async getPersonStaff(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonStaff(id, options)
  }
  async getPersonPersonTypeStaff(id: number, options?: PersonPersonTypeFetchOptions): Promise<PersonPersonTypeStaff> {
    return this.api.getPersonPersonTypeStaff(id, options)
  }
  async getPersonRelationStaff(id: number, options?: PersonRelationFetchOptions): Promise<PersonRelationStaff> {
    return this.api.getPersonRelationStaff(id, options)
  }
  async getPersonRelationTypeStaff(id: number): Promise<PersonRelationTypeStaff> {
    const __cached = this.personRelationTypeStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getPersonRelationTypeStaff(id);
    this.personRelationTypeStaffCache.set(id, __value);
    return __value
  }
  async getPersonReferralStaff(id: number): Promise<PersonReferralStaff> {
    return this.api.getPersonReferralStaff(id)
  }
  async getPersonAddressStaff(id: number, options?: PersonAddressFetchOptions): Promise<PersonAddressStaff> {
    return this.api.getPersonAddressStaff(id, options)
  }
  async getPersonPhoneStaff(id: number, options?: PersonPhoneFetchOptions): Promise<PersonPhoneStaff> {
    return this.api.getPersonPhoneStaff(id, options)
  }
  async getPersonCommentStaff(id: number, options?: PersonCommentFetchOptions): Promise<PersonCommentStaff> {
    return this.api.getPersonCommentStaff(id, options)
  }
  async getPersonTagStaff(id: number, options?: PersonTagFetchOptions): Promise<PersonTagStaff> {
    return this.api.getPersonTagStaff(id, options)
  }
  async getSkillStaff(id: number): Promise<SkillStaff> {
    const __cached = this.skillStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getSkillStaff(id);
    this.skillStaffCache.set(id, __value);
    return __value
  }
  async getAssessmentStaff(id: number): Promise<AssessmentStaff> {
    const __cached = this.assessmentStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getAssessmentStaff(id);
    this.assessmentStaffCache.set(id, __value);
    return __value
  }
  async getSkillAssessmentStaff(id: number, options?: SkillAssessmentFetchOptions): Promise<SkillAssessmentStaff> {
    const __cached = this.skillAssessmentStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getSkillAssessmentStaff(id, options);
    this.skillAssessmentStaffCache.set(id, __value);
    return __value
  }
  async getPersonSkillStaff(id: number, options?: PersonSkillFetchOptions): Promise<PersonSkillStaff> {
    return this.api.getPersonSkillStaff(id, options)
  }
  async getPersonSkillChangeStaff(id: number, options?: PersonSkillChangeFetchOptions): Promise<PersonSkillChangeStaff> {
    return this.api.getPersonSkillChangeStaff(id, options)
  }
  async getRatingChangeStaff(id: number, options?: RatingChangeFetchOptions): Promise<RatingChangeStaff> {
    return this.api.getRatingChangeStaff(id, options)
  }
  async getPersonAgreementInstanceStaff(id: number, options?: PersonAgreementInstanceFetchOptions): Promise<PersonAgreementInstanceStaff> {
    return this.api.getPersonAgreementInstanceStaff(id, options)
  }
  async getSignoutMember(id: number, options?: SignoutMemberFetchOptions): Promise<SignoutMember> {
    return this.api.getSignoutMember(id, options)
  }
  async getSignoutStaff(id: number, options?: SignoutFetchOptions): Promise<SignoutStaff> {
    return this.api.getSignoutStaff(id, options)
  }
  async getSignoutCrewStaff(id: number, options?: SignoutCrewFetchOptions): Promise<SignoutCrewStaff> {
    return this.api.getSignoutCrewStaff(id, options)
  }
  async getAllPersonRelationTypeStaffs(): Promise<Array<PersonRelationTypeStaff>> {
    return this.api.getAllPersonRelationTypeStaffs()
  }
  async getAllSkillStaffs(): Promise<Array<SkillStaff>> {
    return this.api.getAllSkillStaffs()
  }
  async getAllAssessmentStaffs(): Promise<Array<AssessmentStaff>> {
    return this.api.getAllAssessmentStaffs()
  }
  async getAllSkillAssessmentStaffs(options?: SkillAssessmentFetchOptions): Promise<Array<SkillAssessmentStaff>> {
    return this.api.getAllSkillAssessmentStaffs(options)
  }
  async createPersonMember(source: CreatePersonMember): Promise<PersonMember> {
    return this.api.createPersonMember(source)
  }
  async createPersonStaff(source: CreatePersonStaff): Promise<PersonStaff> {
    return this.api.createPersonStaff(source)
  }
  async createPersonPersonTypeStaff(source: CreatePersonPersonType): Promise<PersonPersonTypeStaff> {
    return this.api.createPersonPersonTypeStaff(source)
  }
  async createPersonRelationStaff(source: CreatePersonRelation): Promise<PersonRelationStaff> {
    return this.api.createPersonRelationStaff(source)
  }
  async createPersonReferralStaff(source: CreatePersonReferral): Promise<PersonReferralStaff> {
    return this.api.createPersonReferralStaff(source)
  }
  async createPersonAddressStaff(source: CreatePersonAddress): Promise<PersonAddressStaff> {
    return this.api.createPersonAddressStaff(source)
  }
  async createPersonPhoneStaff(source: CreatePersonPhone): Promise<PersonPhoneStaff> {
    return this.api.createPersonPhoneStaff(source)
  }
  async updatePersonMember(source: UpdatePersonMember): Promise<PersonMember> {
    return this.api.updatePersonMember(source)
  }
  async updatePersonStaff(source: UpdatePersonStaff): Promise<PersonStaff> {
    return this.api.updatePersonStaff(source)
  }
  async updatePersonPersonTypeStaff(source: UpdatePersonPersonType): Promise<PersonPersonTypeStaff> {
    return this.api.updatePersonPersonTypeStaff(source)
  }
  async updatePersonRelationStaff(source: UpdatePersonRelation): Promise<PersonRelationStaff> {
    return this.api.updatePersonRelationStaff(source)
  }
  async updatePersonReferralStaff(source: UpdatePersonReferral): Promise<PersonReferralStaff> {
    return this.api.updatePersonReferralStaff(source)
  }
  async updatePersonAddressStaff(source: UpdatePersonAddress): Promise<PersonAddressStaff> {
    return this.api.updatePersonAddressStaff(source)
  }
  async updatePersonPhoneStaff(source: UpdatePersonPhone): Promise<PersonPhoneStaff> {
    return this.api.updatePersonPhoneStaff(source)
  }
  async deletePersonStaff(source: DeletePersonStaff): Promise<void> {
    return this.api.deletePersonStaff(source)
  }
  async deletePersonPersonTypeStaff(source: DeletePersonPersonTypeStaff): Promise<void> {
    return this.api.deletePersonPersonTypeStaff(source)
  }
  async deletePersonRelationStaff(source: DeletePersonRelationStaff): Promise<void> {
    return this.api.deletePersonRelationStaff(source)
  }
  async deletePersonReferralStaff(source: DeletePersonReferralStaff): Promise<void> {
    return this.api.deletePersonReferralStaff(source)
  }
  async deletePersonAddressStaff(source: DeletePersonAddressStaff): Promise<void> {
    return this.api.deletePersonAddressStaff(source)
  }
  async deletePersonPhoneStaff(source: DeletePersonPhoneStaff): Promise<void> {
    return this.api.deletePersonPhoneStaff(source)
  }
  async searchPersonMember(params: PersonMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonMemberSearchResult>> {
    return this.api.searchPersonMember(params, page, perPage)
  }
  async searchPersonStaff(params: PersonStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonStaffSearchResult>> {
    return this.api.searchPersonStaff(params, page, perPage)
  }
  async searchPersonRelationStaff(params: PersonRelationSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonRelationStaffSearchResult>> {
    return this.api.searchPersonRelationStaff(params, page, perPage)
  }
  async searchPersonReferralStaff(params: PersonReferralSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonReferralStaffSearchResult>> {
    return this.api.searchPersonReferralStaff(params, page, perPage)
  }
  async searchPersonAddressStaff(params: PersonAddressSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonAddressStaffSearchResult>> {
    return this.api.searchPersonAddressStaff(params, page, perPage)
  }
  async searchPersonPhoneStaff(params: PersonPhoneSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonPhoneStaffSearchResult>> {
    return this.api.searchPersonPhoneStaff(params, page, perPage)
  }
  async searchPersonCommentStaff(params: PersonCommentSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonCommentStaffSearchResult>> {
    return this.api.searchPersonCommentStaff(params, page, perPage)
  }
  async searchPersonTagStaff(params: PersonTagSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonTagStaffSearchResult>> {
    return this.api.searchPersonTagStaff(params, page, perPage)
  }
  async searchSkillStaff(params: SkillSearchParams, page: number, perPage: number): Promise<PaginationResult<SkillStaffSearchResult>> {
    return this.api.searchSkillStaff(params, page, perPage)
  }
  async searchPersonSkillStaff(params: PersonSkillSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonSkillStaffSearchResult>> {
    return this.api.searchPersonSkillStaff(params, page, perPage)
  }
  async searchPersonSkillChangeStaff(params: PersonSkillChangeSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonSkillChangeStaffSearchResult>> {
    return this.api.searchPersonSkillChangeStaff(params, page, perPage)
  }
  async searchRatingChangeStaff(params: RatingChangeSearchParams, page: number, perPage: number): Promise<PaginationResult<RatingChangeStaffSearchResult>> {
    return this.api.searchRatingChangeStaff(params, page, perPage)
  }
  async searchPersonAgreementInstanceStaff(params: PersonAgreementInstanceSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonAgreementInstanceStaffSearchResult>> {
    return this.api.searchPersonAgreementInstanceStaff(params, page, perPage)
  }
  async searchSignoutMember(params: SignoutMemberSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutMemberSearchResult>> {
    return this.api.searchSignoutMember(params, page, perPage)
  }
  async searchSignoutStaff(params: SignoutSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutStaffSearchResult>> {
    return this.api.searchSignoutStaff(params, page, perPage)
  }
  async searchSignoutCrewStaff(params: SignoutCrewSearchParams, page: number, perPage: number): Promise<PaginationResult<SignoutCrewStaffSearchResult>> {
    return this.api.searchSignoutCrewStaff(params, page, perPage)
  }
  async searchPersonMemberFull(params: PersonMemberSearchParams, page: number, perPage: number, options?: PersonMemberFetchOptions): Promise<PaginationResult<PersonMember>> {
    return this.api.searchPersonMemberFull(params, page, perPage, options)
  }
  async searchPersonStaffFull(params: PersonStaffSearchParams, page: number, perPage: number, options?: PersonFetchOptions): Promise<PaginationResult<PersonStaff>> {
    return this.api.searchPersonStaffFull(params, page, perPage, options)
  }
  async searchPersonRelationStaffFull(params: PersonRelationSearchParams, page: number, perPage: number, options?: PersonRelationFetchOptions): Promise<PaginationResult<PersonRelationStaff>> {
    return this.api.searchPersonRelationStaffFull(params, page, perPage, options)
  }
  async searchPersonReferralStaffFull(params: PersonReferralSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonReferralStaff>> {
    return this.api.searchPersonReferralStaffFull(params, page, perPage)
  }
  async searchPersonAddressStaffFull(params: PersonAddressSearchParams, page: number, perPage: number, options?: PersonAddressFetchOptions): Promise<PaginationResult<PersonAddressStaff>> {
    return this.api.searchPersonAddressStaffFull(params, page, perPage, options)
  }
  async searchPersonPhoneStaffFull(params: PersonPhoneSearchParams, page: number, perPage: number, options?: PersonPhoneFetchOptions): Promise<PaginationResult<PersonPhoneStaff>> {
    return this.api.searchPersonPhoneStaffFull(params, page, perPage, options)
  }
  async searchPersonCommentStaffFull(params: PersonCommentSearchParams, page: number, perPage: number, options?: PersonCommentFetchOptions): Promise<PaginationResult<PersonCommentStaff>> {
    return this.api.searchPersonCommentStaffFull(params, page, perPage, options)
  }
  async searchPersonTagStaffFull(params: PersonTagSearchParams, page: number, perPage: number, options?: PersonTagFetchOptions): Promise<PaginationResult<PersonTagStaff>> {
    return this.api.searchPersonTagStaffFull(params, page, perPage, options)
  }
  async searchSkillStaffFull(params: SkillSearchParams, page: number, perPage: number): Promise<PaginationResult<SkillStaff>> {
    return this.api.searchSkillStaffFull(params, page, perPage)
  }
  async searchPersonSkillStaffFull(params: PersonSkillSearchParams, page: number, perPage: number, options?: PersonSkillFetchOptions): Promise<PaginationResult<PersonSkillStaff>> {
    return this.api.searchPersonSkillStaffFull(params, page, perPage, options)
  }
  async searchPersonSkillChangeStaffFull(params: PersonSkillChangeSearchParams, page: number, perPage: number, options?: PersonSkillChangeFetchOptions): Promise<PaginationResult<PersonSkillChangeStaff>> {
    return this.api.searchPersonSkillChangeStaffFull(params, page, perPage, options)
  }
  async searchRatingChangeStaffFull(params: RatingChangeSearchParams, page: number, perPage: number, options?: RatingChangeFetchOptions): Promise<PaginationResult<RatingChangeStaff>> {
    return this.api.searchRatingChangeStaffFull(params, page, perPage, options)
  }
  async searchPersonAgreementInstanceStaffFull(params: PersonAgreementInstanceSearchParams, page: number, perPage: number, options?: PersonAgreementInstanceFetchOptions): Promise<PaginationResult<PersonAgreementInstanceStaff>> {
    return this.api.searchPersonAgreementInstanceStaffFull(params, page, perPage, options)
  }
  async searchSignoutMemberFull(params: SignoutMemberSearchParams, page: number, perPage: number, options?: SignoutMemberFetchOptions): Promise<PaginationResult<SignoutMember>> {
    return this.api.searchSignoutMemberFull(params, page, perPage, options)
  }
  async searchSignoutStaffFull(params: SignoutSearchParams, page: number, perPage: number, options?: SignoutFetchOptions): Promise<PaginationResult<SignoutStaff>> {
    return this.api.searchSignoutStaffFull(params, page, perPage, options)
  }
  async searchSignoutCrewStaffFull(params: SignoutCrewSearchParams, page: number, perPage: number, options?: SignoutCrewFetchOptions): Promise<PaginationResult<SignoutCrewStaff>> {
    return this.api.searchSignoutCrewStaffFull(params, page, perPage, options)
  }
  async getPersonMemberMemberships(id: number, options?: MembershipMemberFetchOptions): Promise<Array<MembershipMember>> {
    return this.api.getPersonMemberMemberships(id, options)
  }
  async getPersonStaffReferrals(id: number): Promise<Array<PersonReferralStaff>> {
    return this.api.getPersonStaffReferrals(id)
  }
  async getPersonStaffDamageWaivers(id: number, options?: DamageWaiverFetchOptions): Promise<Array<DamageWaiverStaff>> {
    return this.api.getPersonStaffDamageWaivers(id, options)
  }
  async getPersonStaffPhones(id: number, options?: PersonPhoneFetchOptions): Promise<Array<PersonPhoneStaff>> {
    return this.api.getPersonStaffPhones(id, options)
  }
  async getPersonStaffPersonRatings(id: number, options?: PersonRatingFetchOptions): Promise<Array<PersonRatingStaff>> {
    return this.api.getPersonStaffPersonRatings(id, options)
  }
  async getPersonStaffMemberships(id: number, options?: MembershipStaffFetchOptions): Promise<Array<MembershipStaff>> {
    return this.api.getPersonStaffMemberships(id, options)
  }
  async getPersonStaffAddresses(id: number, options?: PersonAddressFetchOptions): Promise<Array<PersonAddressStaff>> {
    return this.api.getPersonStaffAddresses(id, options)
  }
  async getPersonStaffCards(id: number, options?: CardFetchOptions): Promise<Array<CardStaff>> {
    return this.api.getPersonStaffCards(id, options)
  }
  async getPersonStaffPersonTypes(id: number, options?: PersonPersonTypeFetchOptions): Promise<Array<PersonPersonTypeStaff>> {
    return this.api.getPersonStaffPersonTypes(id, options)
  }
  async getPersonStaffApClassesInstructing(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    return this.api.getPersonStaffApClassesInstructing(id, options)
  }
  async getPersonStaffJpClassesInstructing(id: number, options?: JpClassFetchOptions): Promise<Array<JpClassStaff>> {
    return this.api.getPersonStaffJpClassesInstructing(id, options)
  }
  async getPersonStaffApClassSignups(id: number, options?: ApClassSignupStaffFetchOptions): Promise<Array<ApClassSignupStaff>> {
    return this.api.getPersonStaffApClassSignups(id, options)
  }
  async getPersonStaffJpClassSignups(id: number, options?: JpClassSignupFetchOptions): Promise<Array<JpClassSignupStaff>> {
    return this.api.getPersonStaffJpClassSignups(id, options)
  }
  async getPersonStaffApClassTypeOverrides(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<Array<ApClassTypeOverrideStaff>> {
    return this.api.getPersonStaffApClassTypeOverrides(id, options)
  }
  async getPersonStaffJpClassTypeOverrides(id: number, options?: JpClassTypeOverrideFetchOptions): Promise<Array<JpClassTypeOverrideStaff>> {
    return this.api.getPersonStaffJpClassTypeOverrides(id, options)
  }
  async getPersonStaffRelationsA(id: number, options?: PersonRelationFetchOptions): Promise<Array<PersonRelationStaff>> {
    return this.api.getPersonStaffRelationsA(id, options)
  }
  async getPersonStaffRelationsB(id: number, options?: PersonRelationFetchOptions): Promise<Array<PersonRelationStaff>> {
    return this.api.getPersonStaffRelationsB(id, options)
  }
  async getPersonStaffCheckIns(id: number, options?: PersonCheckInFetchOptions): Promise<Array<PersonCheckInStaff>> {
    return this.api.getPersonStaffCheckIns(id, options)
  }
  async getPersonPersonTypeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonPersonTypeStaffPersonId(id, options)
  }
  async getPersonPersonTypeStaffTypeId(id: number): Promise<PersonTypeStaff> {
    return this.api.getPersonPersonTypeStaffTypeId(id)
  }
  async getPersonRelationStaffAPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getPersonRelationStaffAPersonId(id, options)
  }
  async getPersonRelationStaffBPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getPersonRelationStaffBPersonId(id, options)
  }
  async getPersonAddressStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonAddressStaffPersonId(id, options)
  }
  async getPersonAddressStaffAddressId(id: number): Promise<AddressStaff> {
    return this.api.getPersonAddressStaffAddressId(id)
  }
  async getPersonPhoneStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonPhoneStaffPersonId(id, options)
  }
  async getPersonPhoneStaffPhoneId(id: number): Promise<PhoneStaff> {
    return this.api.getPersonPhoneStaffPhoneId(id)
  }
  async getPersonCommentStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonCommentStaffPersonId(id, options)
  }
  async getPersonTagStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getPersonTagStaffPersonId(id, options)
  }
  async getPersonTagStaffTagId(id: number): Promise<TagStaff | null> {
    return this.api.getPersonTagStaffTagId(id)
  }
  async getSkillAssessmentStaffSkillId(id: number): Promise<SkillStaff> {
    return this.api.getSkillAssessmentStaffSkillId(id)
  }
  async getSkillAssessmentStaffAssessmentId(id: number): Promise<AssessmentStaff> {
    return this.api.getSkillAssessmentStaffAssessmentId(id)
  }
  async getPersonSkillStaffSkillId(id: number): Promise<SkillStaff> {
    return this.api.getPersonSkillStaffSkillId(id)
  }
  async getPersonSkillStaffAssessmentId(id: number): Promise<AssessmentStaff> {
    return this.api.getPersonSkillStaffAssessmentId(id)
  }
  async getPersonSkillStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonSkillStaffPersonId(id, options)
  }
  async getPersonSkillChangeStaffAssignId(id: number, options?: PersonSkillFetchOptions): Promise<PersonSkillStaff> {
    return this.api.getPersonSkillChangeStaffAssignId(id, options)
  }
  async getPersonSkillChangeStaffAssessmentId(id: number): Promise<AssessmentStaff | null> {
    return this.api.getPersonSkillChangeStaffAssessmentId(id)
  }
  async getPersonSkillChangeStaffSkillId(id: number): Promise<SkillStaff> {
    return this.api.getPersonSkillChangeStaffSkillId(id)
  }
  async getPersonSkillChangeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonSkillChangeStaffPersonId(id, options)
  }
  async getRatingChangeStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getRatingChangeStaffPersonId(id, options)
  }
  async getRatingChangeStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getRatingChangeStaffRatingId(id, options)
  }
  async getRatingChangeStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    return this.api.getRatingChangeStaffProgramId(id)
  }
  async getPersonAgreementInstanceStaffAgreementInstanceId(id: number, options?: AgreementInstanceFetchOptions): Promise<AgreementInstanceStaff> {
    return this.api.getPersonAgreementInstanceStaffAgreementInstanceId(id, options)
  }
  async getPersonAgreementInstanceStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff | null> {
    return this.api.getPersonAgreementInstanceStaffAgreementId(id, options)
  }
  async getPersonAgreementInstanceStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonAgreementInstanceStaffPersonId(id, options)
  }
  async getSignoutStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getSignoutStaffPersonId(id, options)
  }
  async getSignoutStaffProgramId(id: number): Promise<ProgramTypeStaff> {
    return this.api.getSignoutStaffProgramId(id)
  }
  async getSignoutStaffBoatId(id: number): Promise<BoatTypeStaff> {
    return this.api.getSignoutStaffBoatId(id)
  }
  async getSignoutStaffTestRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getSignoutStaffTestRatingId(id, options)
  }
  async getSignoutCrewStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getSignoutCrewStaffPersonId(id, options)
  }
  async getSignoutCrewStaffSignoutId(id: number, options?: SignoutFetchOptions): Promise<SignoutStaff | null> {
    return this.api.getSignoutCrewStaffSignoutId(id, options)
  }
  async createChildMember(request: MemberProfileCreate): Promise<MemberProfile> {
    return this.api.createChildMember(request)
  }
  async createEmergencyContactMember(request: PersonCreateEmergencyContactRequest): Promise<MemberProfile> {
    return this.api.createEmergencyContactMember(request)
  }
  async createStaffProfileStaff(request: StaffProfileCreateRequest): Promise<PersonStaff> {
    return this.api.createStaffProfileStaff(request)
  }
  async currentPersonMember(): Promise<PersonMember> {
    return this.api.currentPersonMember()
  }
  async findPersonByNameStaff(name: string): Promise<PersonStaff> {
    return this.api.findPersonByNameStaff(name)
  }
  async getChildrenStaff(ref: PersonRef): Promise<Array<PersonStaff>> {
    return this.api.getChildrenStaff(ref)
  }
  async getChildrenMember(ref: PersonRef): Promise<Array<PersonMember>> {
    return this.api.getChildrenMember(ref)
  }
  async getEmergencyContactsStaff(ref: PersonRef): Promise<Array<PersonStaff>> {
    return this.api.getEmergencyContactsStaff(ref)
  }
  async getEmergencyContactsMember(ref: PersonRef): Promise<Array<PersonMember>> {
    return this.api.getEmergencyContactsMember(ref)
  }
  async getPersonNameStaff(request: PersonGetPersonNameRequest): Promise<string> {
    return this.api.getPersonNameStaff(request)
  }
  async getProfileMember(person: PersonRef): Promise<MemberProfile> {
    return this.api.getProfileMember(person)
  }
  async grantRatingStaff(request: PersonGrantRatingRequest): Promise<void> {
    return this.api.grantRatingStaff(request)
  }
  async removeCommentStaff(request: PersonRemoveCommentRequest): Promise<void> {
    return this.api.removeCommentStaff(request)
  }
  async removePersonAddressStaff(request: PersonRemovePersonAddressRequest): Promise<void> {
    return this.api.removePersonAddressStaff(request)
  }
  async removePersonPhoneStaff(request: PersonRemovePersonPhoneRequest): Promise<void> {
    return this.api.removePersonPhoneStaff(request)
  }
  async removeSkillStaff(request: PersonRemoveSkillRequest): Promise<void> {
    return this.api.removeSkillStaff(request)
  }
  async rescindRatingStaff(request: PersonRescindRatingRequest): Promise<void> {
    return this.api.rescindRatingStaff(request)
  }
  async saveCommentStaff(request: PersonSaveCommentRequest): Promise<PersonCommentStaff> {
    return this.api.saveCommentStaff(request)
  }
  async savePersonAddressStaff(request: SavePersonAddress): Promise<PersonAddressStaff> {
    return this.api.savePersonAddressStaff(request)
  }
  async savePersonPhoneStaff(request: SavePersonPhone): Promise<PersonPhoneStaff> {
    return this.api.savePersonPhoneStaff(request)
  }
  async saveSkillStaff(request: PersonSaveSkillRequest): Promise<PersonSkillStaff> {
    return this.api.saveSkillStaff(request)
  }
  async searchPersonAgreementsStaff(request: PersonSearchPersonAgreementsRequest): Promise<PersonAgreementsPage> {
    return this.api.searchPersonAgreementsStaff(request)
  }
  async setAdminHoldStaff(request: PersonSetAdminHoldRequest): Promise<void> {
    return this.api.setAdminHoldStaff(request)
  }
  async setTagsStaff(request: PersonSetTagsRequest): Promise<void> {
    return this.api.setTagsStaff(request)
  }
  async signAgreementStaff(request: PersonSignAgreementRequest): Promise<PersonAgreementInstanceStaff> {
    return this.api.signAgreementStaff(request)
  }
  async swapRelationDirectionStaff(relation: PersonRelationRef): Promise<PersonRelationStaff> {
    return this.api.swapRelationDirectionStaff(relation)
  }
  async updateProfileMember(request: MemberProfileUpdate): Promise<MemberProfile> {
    return this.api.updateProfileMember(request)
  }
  async updateStaffProfileStaff(request: StaffProfileUpdateRequest): Promise<PersonStaff> {
    return this.api.updateStaffProfileStaff(request)
  }
}
